import { ScreenSpaceEventType, ScreenSpaceEventHandler } from "@cesium/engine";
import AnalysisSplit from "../analysis/AnalysisSplit";
import Utils from "../basic/Utils";

/**
 * 분할 기능을 위한 사용자 입력(마우스/키보드) 클래스.</br>
 * User input (mouse/keyboard) class for object split.
 * @alias InteractionSplit
 *
 * @constructor
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 *
 *
 * @example
 * // 샌드캐슬 예제 'DiTAP - Split' 참조
 *
 * const viewer = new Ditap.DitapViewer("ditapContainer", {
 *   baseLayerPicker: true,
 * });
 *
 * // InteractionSplit 생성
 * const interactionSplit = new Ditap.InteractionSplit(viewer);
 *
 * // interactionSplit handler 실행 버튼
 * Sandcastle.addToolbarButton("Split On", async () => {
 *   // interactionSplit handler 실행
 *   interactionSplit.on(viewer);
 *
 *   // AnalysisSplit 인스턴스 생성
 *   const analysisSplit = interactionSplit.analysisSplit;
 *
 *   // 뷰어에 추가할 데이터 생성
 *   // 1. 정사영상
 *   // 정사영상 인스턴스 생성
 *   const imagery = new Ditap.NationwideOrthoImageryProvider();
 *
 *   // 2. 모델
 *   // 모델 좌표
 *   const modelPosition = new Ditap.Cartesian3.fromDegrees(
 *     126.9782349646649,
 *     37.56671536989183,
 *     100
 *   );
 *
 *   // 모델 행렬 생성
 *   const modelMatrix = Ditap.Transforms.eastNorthUpToFixedFrame(
 *     modelPosition
 *   );
 *
 *   // 모델 인스턴스 생성
 *   const model = await Ditap.Model.fromGltfAsync({
 *     id: "model_01",
 *     url: "../../SampleData/models/GroundVehicle/GroundVehicle.glb",
 *     modelMatrix: modelMatrix,
 *     minimumPixelSize: 128,
 *   });
 *
 *   // 분할된 뷰어에 생성된 인스턴스 추가
 *   // 우측 분할 뷰어에 정사영상 추가
 *   const rightImagery = analysisSplit.addImagery(
 *     Ditap.SplitDirection.RIGHT,
 *     imagery
 *   );
 *
 *   // 좌측 분할 뷰어에 모델 추가
 *   const leftModel = analysisSplit.addModel(
 *     Ditap.SplitDirection.LEFT,
 *     model
 *   );
 *
 *   // 모델 위치로 카메라 이동
 *   viewer.camera.flyTo({
 *     destination: Ditap.Cartesian3.fromDegrees(
 *       126.9782349646649,
 *       37.5645,
 *       150
 *     ),
 *     duration: 2,
 *     orientation: {
 *       heading: Ditap.Math.toRadians(0),
 *       pitch: Ditap.Math.toRadians(-10),
 *       roll: 0,
 *     },
 *   });
 * });
 *
 * // interactionSplit handler 종료 버튼
 * Sandcastle.addToolbarButton("Split Off", () => {
 *   // interactionSplit handler 종료
 *   // interactionSplit.off() - 모든 분할 뷰어에 생성된 객체가 제거됩니다.
 *   // interactionSplit.off(Ditap.SplitDirection.LEFT) - 좌측 분할 뷰어에 생성된 객체가 유지됩니다.
 *   // interactionSplit.off(Ditap.SplitDirection.RIGHT) - 우측 분할 뷰어에 생성된 객체가 유지됩니다.
 *   // interactionSplit.off(Ditap.SplitDirection.BOTH) - 좌측, 우측 모든 분할 뷰어에 생성된 객체가 유지됩니다.
 *   interactionSplit.off();
 * });
 *
 */
class InteractionSplit {
  constructor(viewer) {
    this._viewer = viewer;
    this._analysisSplit = undefined;
    this._isActiveSlider = false;
    this._isOn = false;
    this._handler = undefined;
    this._buttonElement = undefined;
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    this._viewer = viewer;
  }
  get viewer() {
    return this._viewer;
  }

  /**
   * 분할을 위한 분석 기능 클래스.</br>
   * Analysis feature class for object deletion.
   * @type {AnalysisSplit}
   * @readonly
   */
  set analysisSplit(analysisSplit) {
    if (analysisSplit instanceof AnalysisSplit || analysisSplit === undefined) {
      this._analysisSplit = analysisSplit;
    } else {
      this._isActiveSlider = false;
      console.error("InteractionSplit.js: Type of parameter doesn't match");
    }
  }
  get analysisSplit() {
    return this._analysisSplit;
  }

  /*
   * 분할 슬라이더 비/활성화.</br>
   * Split slider enabled or disabled.
   * @type {boolean}
   */
  set isActiveSlider(isActiveSlider) {
    this._isActiveSlider = isActiveSlider;
  }
  get isActiveSlider() {
    return this._isActiveSlider;
  }

  /*
   * 핸들러 비/활성화.</br>
   * Handler enabled or disabled.
   * @type {boolean}
   */
  set isOn(isOn) {
    if (typeof isOn === "boolean") {
      this._isOn = isOn;
    } else {
      this._isOn = false;
      console.error("InteractionSplit.js: Type of parameter no match ");
    }
  }

  /**
   * 핸들러 비/활성화.</br>
   * Check handler enable/disable status.
   * @type {boolean}
   * @readonly
   */
  get isOn() {
    return this._isOn;
  }

  /*
   * 사용자 입력 이벤트 처리 핸들러.</br>
   * Handler for processing user input events.
   * @type {ScreenSpaceEventHandler}
   */
  set handler(handler) {
    this._handler = handler;
  }
  get handler() {
    return this._handler;
  }

  /*
   * 사용자 입력 클래스 활성화 버튼.</br>
   * Button that activates the user input class.
   * @type {Element}
   */
  set buttonElement(buttonElement) {
    this._buttonElement = buttonElement;
  }
  get buttonElement() {
    return this._buttonElement;
  }

  /*
   * 분할 위치 조회.<br>
   * Get split position.
   * @param {ScreenSpaceEventHandler.PositionedEvent} moveEvent 이동 이벤트.Event raised in handler
   * @returns {string} 분할 위치.<br>Split position.
   */
  getSplitPosition(position) {
    if (!this.isActiveSlider) {
      return;
    }
    const slider = this.analysisSplit.slider;
    const relativeOffset = position;
    const splitPosition =
      (slider.offsetLeft + relativeOffset) / slider.parentElement.offsetWidth;

    return splitPosition;
  }

  /**
   * 터치 기능 활성화.</br>
   * Enable touch functionality.
   */
  createTouch() {
    const slider = this.analysisSplit.slider;

    let isDragging = true;

    // 터치 시작 이벤트 핸들러를 등록합니다.
    slider.addEventListener(
      "touchstart",
      (this.touchStart = (event) => {
        isDragging = true;
      })
    );

    // 터치 이동 이벤트 핸들러를 등록합니다.
    slider.addEventListener(
      "touchmove",
      (this.touchMove = (event) => {
        if (!isDragging) {
          return;
        }
        const clientX = event.touches[0].clientX;
        const slider = parseInt(this.analysisSplit.slider.offsetWidth);
        const container = parseInt(this.viewer.container.offsetWidth);
        const maxLeft = container - slider / 2;
        const minLeft = slider / 2;

        if (clientX > minLeft && clientX < maxLeft) {
          this.analysisSplit.slider.style.left = `${clientX - slider / 2}px`;
          const position = clientX / container;
          this.analysisSplit.splitPosition = position;
        }
      })
    );

    // 터치 종료 이벤트 핸들러를 등록합니다.
    slider.addEventListener(
      "touchend",
      (this.touchEnd = (event) => {
        // 드래그 상태를 해제합니다.
        isDragging = false;
      })
    );
  }

  /**
   * InteracionSplit on/off 기능.</br>
   * Function on and off.
   */
  toggle() {
    if (!this.isOn) {
      this.on();
      this.isOn = true;
    } else {
      this.off();
      this.isOn = false;
    }
  }

  /**
   * 분할 핸들러 실행.</br>
   * Execute split handler.
   */
  on() {
    if (!this.isOn) {
      // 버튼 활성화
      this.buttonElement?.classList.add("on");
      this.isOn = true;

      this.analysisSplit = new AnalysisSplit(this.viewer);
      this.analysisSplit.on();

      this.createTouch();
      const slider = this.analysisSplit.slider;
      // 슬라이더 - 핸들러 생성
      this.handler = new ScreenSpaceEventHandler(slider);

      // 슬라이더 - 마우스 클릭 이벤트 추가
      this.handler.setInputAction(() => {
        this.isActiveSlider = true;
      }, ScreenSpaceEventType.LEFT_DOWN);

      // 슬라이더 - 마우스 무브 이벤트 추가
      this.handler.setInputAction((e) => {
        this.moveSlider(e.endPosition.x);
      }, ScreenSpaceEventType.MOUSE_MOVE);

      this.handler.setInputAction(() => {
        this.isActiveSlider = false;
      }, ScreenSpaceEventType.LEFT_UP);
    }
  }

  /*
   * 슬라이더 이동 시 분할 위치 적용.<br>
   * When moving the slider, the division position is applied to the DitapViewer.
   * @param {ScreenSpaceEventHandler.PositionedEvent} moveEvent 이동 이벤트.<br>Event raised in handler
   */
  moveSlider(position) {
    if (!this.isActiveSlider) {
      return;
    }
    const splitPosition = this.getSplitPosition(position);

    let left = 100.0 * splitPosition;

    if (left >= 100) {
      left = 100;
    } else if (left < 0) {
      left = 0;
    }
    this.analysisSplit.slider.style.left = `calc(${left}% - 5px)`;

    this.analysisSplit.splitPosition = splitPosition;
  }

  /**
   * 분할 핸들러 종료.</br>
   * End split handler.
   * @param {SplitDirection} splitDirection
   * 파라미터가 없을 경우, 분할 뷰어에 생성된 모든 객체가 제거됩니다.</br>
   * SplitDirection 파라미터를 전달하면 해당 방향의 분할 뷰어 위치에 생성된 객체가 유지됩니다.</br>
   * When no parameter is provided, all objects created within the split viewer will be removed.<br>
   * Passing the SplitDirection parameter will maintain the objects created at the position of the split viewer in the specified direction.
   */
  off(splitDirection) {
    if (this.isOn) {
      // 버튼 비활성화
      this.destroy();
      this.buttonElement?.classList.remove("on");
      this.isOn = false;

      this.analysisSplit.destroy(splitDirection);

      const isMobile = Utils.detectDeviceType();
      if (!isMobile) {
        // 슬라이더 - 핸들러 삭제
        this.handler?.destroy();
        this.handler = undefined;
      }

      // this.viewer = undefined;
      this.analysisSplit = undefined;
      this.isActiveSlider = false;
    }
  }

  /**
   * 객체의 리소스 파괴.<br>
   * Destroy resources held by this object.
   */
  destroy() {
    const slider = this.analysisSplit.slider;
    if (!slider) {
      return;
    }
    if (this.touchStart) {
      slider.removeEventListener("touchstartEvent", this.touchStart);
    }
    if (this.touchMove) {
      slider.removeEventListener("touchmove", this.touchMove);
    }
    if (this.touchEnd) {
      slider.removeEventListener("touchend", this.touchEnd);
    }
  }
}
export default InteractionSplit;
